import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface HistoryItem {
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class HistoryTrackerService {
  private historyStack: HistoryItem[] = [];
  private currentIndex = -1;

  // Keys used for session storage
  private readonly STORAGE_KEY_STACK = 'historyStack';
  private readonly STORAGE_KEY_INDEX = 'currentIndex';

  constructor(private router: Router) {
    // Try to load existing history from session storage.
    const storedStack = sessionStorage.getItem(this.STORAGE_KEY_STACK);
    const storedIndex = sessionStorage.getItem(this.STORAGE_KEY_INDEX);
    if (storedStack && storedIndex !== null) {
      try {
        this.historyStack = JSON.parse(storedStack) as HistoryItem[];
        this.currentIndex = parseInt(storedIndex, 10);
      } catch (error) {
        console.error('Error parsing session storage data:', error);
        // Reset to default if parsing fails.
        this.historyStack = [];
        this.currentIndex = -1;
      }
    }

    // Listen for Angular Router NavigationEnd events.
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.trackNavigation(event.urlAfterRedirects);
    });
  }

  /**
   * Saves the current historyStack and currentIndex to session storage.
   */
  private updateSessionStorage(): void {
    sessionStorage.setItem(this.STORAGE_KEY_STACK, JSON.stringify(this.historyStack));
    sessionStorage.setItem(this.STORAGE_KEY_INDEX, this.currentIndex.toString());
  }

  /**
   * Tracks a new navigation event and updates the internal history stack.
   * Handles new, forward, and backward navigations.
   *
   * @param url The URL navigated to.
   */
  private trackNavigation(url: string): void {
    // First navigation initialization.
    if (this.currentIndex === -1) {
      this.historyStack.push({ url });
      this.currentIndex = 0;
      this.updateSessionStorage();
      return;
    }

    // If the URL is the same as the current one, do nothing.
    if (this.historyStack[this.currentIndex].url === url) {
      return;
    }

    // Forward navigation:
    if (this.currentIndex < this.historyStack.length - 1 && this.historyStack[this.currentIndex + 1].url === url) {
      this.currentIndex++;
      this.updateSessionStorage();
      return;
    }

    // Backward navigation:
    const backIndex = this.historyStack
      .slice(0, this.currentIndex)
      .map((item) => item.url)
      .lastIndexOf(url);
    if (backIndex !== -1) {
      this.currentIndex = backIndex;
      this.updateSessionStorage();
      return;
    }

    // New navigation:
    // Clear any forward history.
    if (this.currentIndex < this.historyStack.length - 1) {
      this.historyStack = this.historyStack.slice(0, this.currentIndex + 1);
    }
    this.historyStack.push({ url });
    this.currentIndex++;
    this.updateSessionStorage();
  }

  /**
   * Returns the previous history item, or null if there isn't one.
   *
   * @returns The previous HistoryItem or null.
   */
  public getPreviousHistoryItem(): HistoryItem | null {
    return this.currentIndex > 0 ? this.historyStack[this.currentIndex - 1] : null;
  }
}
